import SkeletonLoader from 'bitmovin-web-ui/src/components/generic/skeletonLoader';
import React, {Component, Fragment} from 'react';
import {connect} from 'react-redux';
import styled, {css} from 'styled-components';
import Sugar from 'sugar';

import BlueFireworkPanel from '../../components/blueFireworkPanel';
import {ExternalLink, Link, pxToEm} from '../../components/globalStyles';
import {HexagonSize} from '../../components/hexagonIconImage';
import HexagonLeaflet from '../../components/hexagonLeaflet';
import HexagonLeafletDropdown from '../../components/hexagonLeaflet/hexagonLeafletDropdown';
import SectionDescription from '../../components/sectionDescription';
import HardcodedSalesDemoPath from '../../data/hardcodedSalesDemoPath';
import PartnerData from '../../data/partnerData';
import {SalesDemo, SalesDemoCategory} from '../../logic/api/frontendService/entities';
import {salesDemoCategoryToHeader} from '../../logic/humanizer';
import {AppRoutePathFunction} from '../../logic/routes';
import SalesDemoIconPathRegistry from '../../logic/salesDemoIconPathRegistry';
import {RootState} from '../../logic/store';
import {LoadingState, State as SalesDemosState} from '../../logic/store/salesDemosStore';
import EncodingPartnersLogo from '../../media/partnerIcons/encoding_partners.svg';
import LiveStreamingPartnersLogo from '../../media/partnerIcons/livestream_partners.svg';
import SSAIPartnersLogo from '../../media/partnerIcons/ssai_partners.svg';

//#region Styled Components

const StyledWrapper = styled.div`
  margin-top: ${pxToEm(107)}em;
  margin-bottom: ${pxToEm(55)}em;
`;

const StyledSectionDescription = styled(SectionDescription)`
  margin-bottom: ${pxToEm(72)}em;
  text-align: center;
`;

const StyledBlueFireworkPanel = styled(BlueFireworkPanel)`
  border-radius: 15px;
  padding: ${pxToEm(74)}em ${pxToEm(114)}em;
`;

const categoryFontSizePx = 25;
const StyledCategoryH3 = styled.h3`
  color: white;
  font-size: ${pxToEm(categoryFontSizePx)}em;
  margin-top: ${pxToEm(82) / pxToEm(categoryFontSizePx)}em;
  margin-bottom: ${pxToEm(64) / pxToEm(categoryFontSizePx)}em;

  :first-child {
    margin-top: 0;
  }
`;

const StyledDemoSkeletonLoader = styled(SkeletonLoader)`
  height: ${pxToEm(94)}em;
`;

const StyledCategoryDemosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(25em, 1fr));
  grid-gap: ${pxToEm(30)}em;
`;

const StyledDemoH4 = styled.h4`
  margin: 0;
`;

const StyledPartnersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(18em, 1fr));
  grid-gap: ${pxToEm(13)}em;
  margin-top: ${pxToEm(56)}em;
`;

const PartnerTileStyle = css`
  display: flex;
  justify-content: center;
  align-items: center;
  background: #fefefe;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(16, 87, 131, 0.2);
  padding: ${pxToEm(31)}em ${pxToEm(35)}em;
`;

const StyledPartnerImgWrapperDiv = styled.div`
  ${PartnerTileStyle}

  > img {
    width: 100%;
  }
`;

const StyledLinkedPartnerTile = styled(ExternalLink)`
  ${PartnerTileStyle}
`;

const StyledLabeledDemoH4 = styled(StyledDemoH4)`
  margin-bottom: 0;
`;

const StyledPartnerLabelDiv = styled.div`
  display: flex;
  flex-direction: row;
  height: ${pxToEm(32)}em;
  align-items: center;
  margin-top: 0.5rem;

  span {
    font-size: ${pxToEm(11)}em;
  }

  img {
    width: 100%;
  }
`;

//#endregion

interface MapStateToProps {
  salesDemos?: SalesDemosState;
}

interface Props extends MapStateToProps {
  className?: string;
}

class DemosSection extends Component<Props> {
  public render() {
    return (
      <StyledWrapper>
        <StyledSectionDescription
          headlineType={2}
          headline="Top challenges we help you to solve and stay competitive"
          bottomSubHeadline="Demos featuring our next generation video streaming technology."
        />

        <StyledBlueFireworkPanel
          fireworks={[{width: '70%', top: '-5%', right: '-15%'}, {width: '70%', bottom: '5%', left: '-15%'}]}>
          {this.renderCategory(SalesDemoCategory.HIGHER_QUALITY)}
          {this.renderCategory(SalesDemoCategory.EASE_OF_USE)}
          {this.renderCategory(SalesDemoCategory.INNOVATION)}
          {this.renderCategory(SalesDemoCategory.MONETIZATION)}
          {this.renderCategory(SalesDemoCategory.PARTNERS)}
          {this.renderPartners()}
        </StyledBlueFireworkPanel>
      </StyledWrapper>
    );
  }

  private renderCategory = (category: SalesDemoCategory) => {
    const {loadingState, response} = this.props.salesDemos;

    let salesDemosLinks: JSX.Element[] = [];
    const ssaiDemos: SalesDemo[] = [];
    const livestreamDemos: SalesDemo[] = [];
    const encodingDemos: SalesDemo[] = [];

    if (loadingState === LoadingState.Loading) {
      salesDemosLinks = new Array(6)
        .fill({})
        .map((value, index) => <StyledDemoSkeletonLoader key={`demo-skeleton-loader-${index}`} />);
    } else if (loadingState === LoadingState.Error || loadingState === LoadingState.Default) {
      salesDemosLinks = null;
    } else {
      const allDemos: ReadonlyArray<SalesDemo> = response.demos || [];
      const categoryDemos = allDemos.filter(salesDemo => salesDemo.category === category);
      const sortedCategoryDemos = Sugar.Array.sortBy(categoryDemos, salesDemo => salesDemo.name);

      sortedCategoryDemos.map((demo, i) => {
        if (
          demo.path === HardcodedSalesDemoPath.SsaiYospace ||
          demo.path === HardcodedSalesDemoPath.SsaiGoogleDFP ||
          demo.path === HardcodedSalesDemoPath.SsaiMediaTaylor
        ) {
          ssaiDemos.push(demo);
        } else if (
          demo.path === HardcodedSalesDemoPath.LiveStreamingSRT ||
          demo.path === HardcodedSalesDemoPath.LiveStreamingZixi
        ) {
          livestreamDemos.push(demo);
        } else if (
          demo.path === HardcodedSalesDemoPath.CloudEncodingAws ||
          demo.path === HardcodedSalesDemoPath.CloudEncodingGc
        ) {
          encodingDemos.push(demo);
        } else {
          salesDemosLinks.push(
            <Link to={AppRoutePathFunction.DemoDetail({demoId: demo.id})} key={`sales-demo-${demo.id}-${i}`}>
              <HexagonLeaflet
                hexagonIconSrc={SalesDemoIconPathRegistry.getIconPath(demo.path)}
                hexagonSize={HexagonSize.Normal}
                borderRadius={'8px'}>
                <StyledDemoH4>{demo.name}</StyledDemoH4>
              </HexagonLeaflet>
            </Link>
          );
        }
      });
    }

    const dropdownGroups = [
      {
        demos: ssaiDemos,
        text: 'SSAI using partner content',
        icon: SalesDemoIconPathRegistry.getIconPath(HardcodedSalesDemoPath.Ssai),
        logo: SSAIPartnersLogo
      },
      {
        demos: livestreamDemos,
        text: 'Live streaming using partner content',
        icon: SalesDemoIconPathRegistry.getIconPath(HardcodedSalesDemoPath.LiveStreaming),
        logo: LiveStreamingPartnersLogo
      },
      {
        demos: encodingDemos,
        text: 'Encoding on partner services',
        icon: SalesDemoIconPathRegistry.getIconPath(HardcodedSalesDemoPath.CloudEncodingDemo),
        logo: EncodingPartnersLogo
      }
    ];

    dropdownGroups.forEach((dropdownGroup, dropdownGroupIndex) => {
      if (dropdownGroup.demos.length > 0) {
        salesDemosLinks.push(
          <HexagonLeafletDropdown
            hexagonIconSrc={dropdownGroup.icon}
            borderRadius={'8px'}
            dropdownItems={dropdownGroup.demos.map(demo => ({
              logo: SalesDemoIconPathRegistry.getPartnerLogoIconPath(demo.path),
              route: AppRoutePathFunction.DemoDetail({demoId: demo.id})
            }))}
            key={`hexagon-leaflet-dropdown-${dropdownGroupIndex}`}>
            <StyledLabeledDemoH4>{dropdownGroup.text}</StyledLabeledDemoH4>
            <StyledPartnerLabelDiv>
              <img src={dropdownGroup.logo} />
            </StyledPartnerLabelDiv>
          </HexagonLeafletDropdown>
        );
      }
    });

    return (
      <Fragment>
        <StyledCategoryH3>{salesDemoCategoryToHeader(category)}</StyledCategoryH3>

        <StyledCategoryDemosGrid>{salesDemosLinks}</StyledCategoryDemosGrid>
      </Fragment>
    );
  };

  private renderPartners = () => {
    const partners = Sugar.Array.sortBy(PartnerData, partner => partner.name.toLowerCase());

    return (
      <StyledPartnersGrid>
        {partners.map((partner, i) => (
          <StyledPartnerImgWrapperDiv key={`partner-${i}`} title={partner.name}>
            <img src={partner.logoPath} alt={partner.name} />
          </StyledPartnerImgWrapperDiv>
        ))}
        <StyledLinkedPartnerTile href={'https://bitmovin.com/partners/?partnercat=technology-partners'}>
          <h1>See more...</h1>
        </StyledLinkedPartnerTile>
      </StyledPartnersGrid>
    );
  };
}

const mapStateToProps = (state: RootState): MapStateToProps => ({
  salesDemos: state.salesDemos
});

export default connect(mapStateToProps)(DemosSection);
